import { useCallback, useMemo } from 'react';
import { useUpsertClassUserMutation } from '@/apollo/gql/generated/graphql';
import { useRouter } from 'next/router';
import { useViewerContext } from '@/contexts/useViewer';
import { customBugsnagLogger } from '@/utils/bugsnag';

const useUpdateUser = () => {
	const [upsertClassUser] = useUpsertClassUserMutation();
	const router = useRouter();
	const slug = useMemo(() => router?.query?.id, [router]);
	const { viewer } = useViewerContext();

	const toggleGhost = useCallback(
		(ghost) => {
			customBugsnagLogger.debug(`user ${viewer?.id} setting ghost : ${ghost}`);

			upsertClassUser({
				variables: {
					input: {
						userId: viewer?.id,
						role: ghost ? 'ghost' : viewer?.role,
						classSlug: slug as string,
					},
				},
			});
		},
		[upsertClassUser, slug, viewer]
	);

	const setSuperGhost = useCallback(() => {
		customBugsnagLogger.debug(`user ${viewer?.id} setting super ghost`);

		upsertClassUser({
			variables: {
				input: {
					userId: viewer?.id,
					role: 'super-ghost',
					classSlug: slug as string,
				},
			},
		});
	}, [upsertClassUser, slug, viewer]);

	const toggleHandRaised = useCallback(
		(userId: null, raised) => {
			customBugsnagLogger.debug(`user ${userId} raised hand: ${raised}`);
			upsertClassUser({
				variables: {
					input: {
						userId: userId || viewer?.id,
						handRaised: raised,
						classSlug: slug as string,
					},
				},
			});
		},
		[upsertClassUser, slug, viewer]
	);

	const updatePermissions = useCallback(
		(permissions) => {
			customBugsnagLogger.debug(
				`user ${viewer?.id} changed permissions to : ${permissions}`
			);

			upsertClassUser({
				variables: {
					input: {
						permissions,
						userId: viewer?.id,
						classSlug: slug as string,
					},
				},
			});
		},
		[upsertClassUser, slug, viewer]
	);

	return { toggleGhost, toggleHandRaised, updatePermissions, setSuperGhost };
};

export default useUpdateUser;
